import {
  TokenData,
  UserInfo,
  AuthUrlResponse,
  OAuthCallbackMessage,
  LoginResult,
} from '@/integrations/trimble/types'

/**
 * Client-side authentication manager for Trimble OAuth
 * Handles token storage, validation, and refresh
 */
class TrimbleAuthManager {
  private baseUrl: string
  private tokenKey: string
  private userKey: string

  constructor(baseUrl: string) {
    // Use the dedicated server for Trimble API requests
    this.baseUrl = baseUrl || 'http://localhost:3000'
    this.tokenKey = 'trimble_auth_token'
    this.userKey = 'trimble_user_info'

    //we're using this in functions so we'll need to bind
    this.isAuthenticated = this.isAuthenticated.bind(this)
    this.getUserInfo = this.getUserInfo.bind(this)
    this.getUserInfoDirect = this.getUserInfoDirect.bind(this)
    this.refreshToken = this.refreshToken.bind(this)
    this.login = this.login.bind(this)
    this.logout = this.logout.bind(this)
    this.apiCall = this.apiCall.bind(this)
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    // First check localStorage
    const localToken = this.getStoredToken()
    if (localToken && !this.isTokenExpired(localToken)) {
      return true
    }

    // If no valid local token, check server session
    try {
      const userInfo = await this.getUserInfo()
      if (userInfo && userInfo.authenticated) {
        // Store the token from server to localStorage
        this.storeToken(userInfo)
        return true
      }
    } catch (error) {
      // If server check fails, try refresh if we have a local refresh token
      if (localToken && localToken.refresh_token) {
        return await this.refreshToken()
      }
    }

    return false
  }

  /**
   * Get stored token from localStorage
   */
  getStoredToken(): TokenData | null {
    try {
      const tokenStr = localStorage.getItem(this.tokenKey)
      return tokenStr ? JSON.parse(tokenStr) : null
    } catch (error) {
      console.error('Failed to parse stored token:', error)
      this.clearStoredAuth()
      return null
    }
  }

  /**
   * Store token in localStorage
   */
  storeToken(tokenData: TokenData): void {
    try {
      localStorage.setItem(this.tokenKey, JSON.stringify(tokenData))
      if (tokenData.user_id) {
        const userInfo: UserInfo = {
          user_id: tokenData.user_id,
          authenticated: true,
          expires_at: tokenData.expires_at,
        }
        localStorage.setItem(this.userKey, JSON.stringify(userInfo))
      }
    } catch (error) {
      console.error('Failed to store token:', error)
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: TokenData): boolean {
    if (!token || !token.expires_at) {
      return true
    }

    const expiresAt = new Date(token.expires_at)
    const now = new Date()
    const bufferTime = 5 * 60 * 1000 // 5 minutes buffer

    return expiresAt.getTime() - now.getTime() < bufferTime
  }

  /**
   * Get current user info
   */
  async getUserInfo(): Promise<TokenData | null> {
    try {
      const response = await fetch(`${this.baseUrl}/oauth/user`, {
        credentials: 'include',
      })

      if (response.ok) {
        const userInfo = (await response.json()) as TokenData
        // Always store the latest token info from server
        this.storeToken(userInfo)
        return userInfo
      }
      if (response.status === 401) {
        // Not authenticated on server, clear local storage
        this.clearStoredAuth()
        return null
      }

      return null
    } catch (error) {
      console.error('Failed to get user info:', error)
      return null
    }
  }

  /**
   * Get user information directly from the /oauth/user endpoint
   * This is used to validate the Trimble session
   */
  async getUserInfoDirect(): Promise<TokenData | null> {
    try {
      const response = await fetch(`${this.baseUrl}/oauth/user`, {
        credentials: 'include',
      })

      if (response.ok) {
        const userInfo = (await response.json()) as TokenData
        // Always store the latest token info from server
        this.storeToken(userInfo)
        return userInfo
      }
      if (response.status === 401) {
        // Not authenticated on server, clear local storage
        this.clearStoredAuth()
        return null
      }

      return null
    } catch (error) {
      console.error('Failed to get user info directly:', error)
      return null
    }
  }

  /**
   * Refresh the access token
   */
  async refreshToken(): Promise<boolean> {
    const token = this.getStoredToken()
    if (!token || !token.refresh_token) {
      console.log('No refresh token available')
      this.clearStoredAuth()
      return false
    }

    try {
      const response = await fetch(`${this.baseUrl}/oauth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          refresh_token: token.refresh_token,
        }),
      })

      if (response.ok) {
        const newTokenData = (await response.json()) as TokenData
        this.storeToken(newTokenData)
        console.log('Token refreshed successfully')
        return true
      }
      console.error('Token refresh failed:', response.status)
      this.clearStoredAuth()
      return false
    } catch (error) {
      console.error('Token refresh error:', error)
      this.clearStoredAuth()
      return false
    }
  }

  /**
   * Initiate OAuth login
   * @param {boolean} usePopup - Whether to use popup mode (default: false)
   * @returns {Promise<boolean | void | LoginResult>} - Returns true if login succeeded, false otherwise, or an object with tokenData and showModal flag
   */
  async login(usePopup = true): Promise<boolean | void | LoginResult> {
    if (!usePopup) {
      window.location.href = `${this.baseUrl}/oauth/login`
      return
    }

    try {
      // First get the auth URL
      const response = await fetch(`${this.baseUrl}/oauth/login?mode=popup`)
      const { authUrl } = (await response.json()) as AuthUrlResponse

      // Open popup window
      const width = 600
      const height = 700
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2

      const popup = window.open(
        authUrl,
        'trimble-oauth-popup',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no,resizable=yes,scrollbars=yes,popup=yes`
      )

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.')
      }

      // Create a promise to handle the OAuth callback message
      return await new Promise<boolean>((resolve, reject) => {
        // Listen for OAuth callback message
        const messageHandler = (event: MessageEvent<OAuthCallbackMessage>) => {
          if (event.data && event.data.type === 'oauth-callback') {
            // Clean up
            window.removeEventListener('message', messageHandler)
            popup.close()

            if (event.data.success) {
              // Store the received token data
              const tokenData: TokenData = {
                access_token: event.data.access_token!,
                refresh_token: event.data.refresh_token,
                expires_at: event.data.expires_at!,
                user_id: event.data.user_id!,
                authenticated: true,
              }
              this.storeToken(tokenData)
              resolve(true)
            } else {
              reject(new Error(event.data.error || 'Authentication failed'))
            }
          }
        }

        window.addEventListener('message', messageHandler as EventListener)

        // Check if popup is closed without completing auth
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed)
            window.removeEventListener(
              'message',
              messageHandler as EventListener
            )
            reject(new Error('Authentication cancelled'))
          }
        }, 1000)
      })
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to initiate OAuth: ${error.message}`)
      }
      throw new Error('Failed to initiate OAuth: Unknown error')
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/oauth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.clearStoredAuth()
    }
  }

  /**
   * Clear stored authentication data
   */
  clearStoredAuth(): void {
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem(this.userKey)
  }

  /**
   * Make authenticated API call
   * @param endpoint API endpoint to call
   * @param options Fetch options
   * @returns Promise with the response
   */
  async apiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const isAuth = await this.isAuthenticated()
    if (!isAuth) {
      throw new Error('User not authenticated')
    }

    const defaultOptions: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...defaultOptions,
        ...options,
      })

      if (response.status === 401) {
        // Token might be expired, try refresh
        const refreshed = await this.refreshToken()
        if (refreshed) {
          // Retry the request
          return await fetch(`${this.baseUrl}${endpoint}`, {
            ...defaultOptions,
            ...options,
          })
        }
        throw new Error('Authentication failed')
      }

      return response
    } catch (error) {
      console.error('API call error:', error)
      throw error
    }
  }
}

// Export the class
export default TrimbleAuthManager
